import { useEffect, useMemo, useState } from "react";
import {
  FormItem,
  FormItemOption,
  ValidationResult,
} from "./form-builder.model";
import { TextInput } from "./input/text-input";
import { NumberInput } from "./input/number-input";
import { PasswordInput } from "./input/password-input";
import { LayoutService } from "./layout/layout-service";
import { SelectInput } from "./input/select-input";
import { TextAreaInput } from "./input/textarea-input";
import { JsonInput } from "./input/json-input";
import { CheckboxInput } from "./input/checkbox-input";
import { ToggleInput } from "./input/toggle-input";
import { ComboBoxInput } from "./input/combobox-input";
import { FormWidgetService } from "./widgets/form-widget-service";
import { isEmpty } from "lodash";
import "./form-input.scss";

export type FormInputProps = {
  formLayout: string;
  formItem: FormItem;
  formData: Record<string, unknown>;
  formErrors: Record<string, ValidationResult[] | undefined>;
  setValue: (newValue: unknown) => void;
};

export const FormInput: React.FC<FormInputProps> = ({
  formLayout,
  formItem,
  formData,
  formErrors,
  setValue,
}) => {
  const [resolvedOptions, setResolvedOptions] = useState<FormItemOption[]>([]);

  useEffect(() => {
    let cancelled = false;

    if (formItem.type !== "select" && formItem.type !== "combobox") {
      setResolvedOptions([]);
      return;
    }

    const optionsSource = formItem.options;
    const optionsOrPromise =
      typeof optionsSource === "function" ? optionsSource() : optionsSource;

    Promise.resolve(optionsOrPromise)
      .then((options) => {
        if (!cancelled) {
          setResolvedOptions(options ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [formItem]);

  const formInput = useMemo(() => {
    if (formItem.render)
      return <>{formItem.render({ value: formData[formItem.name], onChange: setValue, values: formData })}</>;
    switch (formItem.type) {
      case "text":
        return (
          <TextInput
            formItem={formItem}
            formData={formData}
            formErrors={formErrors}
            setValue={setValue}
          />
        );
      case "textarea":
        return (
          <TextAreaInput
            formItem={formItem}
            formData={formData}
            formErrors={formErrors}
            setValue={setValue}
          />
        );
      case "json":
        return (
          <JsonInput
            formItem={formItem}
            formData={formData}
            formErrors={formErrors}
            setValue={setValue}
          />
        );
      case "number":
        return (
          <NumberInput
            formItem={formItem}
            formData={formData}
            formErrors={formErrors}
            setValue={setValue}
          />
        );
      case "password":
        return (
          <PasswordInput
            formItem={formItem}
            formData={formData}
            formErrors={formErrors}
            setValue={setValue}
          />
        );
      case "checkbox":
        return (
          <CheckboxInput
            formItem={formItem}
            formData={formData}
            formErrors={formErrors}
            setValue={setValue}
          />
        );
      case "toggle":
        return (
          <ToggleInput
            formItem={formItem}
            formData={formData}
            formErrors={formErrors}
            setValue={setValue}
          />
        );
      case "select":
        return (
          <SelectInput
            formItem={formItem}
            formData={formData}
            formErrors={formErrors}
            setValue={setValue}
            options={resolvedOptions}
          />
        );
      case "combobox":
        return (
          <ComboBoxInput
            formItem={formItem}
            formData={formData}
            formErrors={formErrors}
            setValue={setValue}
            options={resolvedOptions}
          />
        );
      case "widget": {
        const widget = FormWidgetService.get().get(formItem.widget);
        if (!widget) {
          console.error(
            `No form widget registered for "${formItem.widget}" (field "${formItem.name}")`,
          );
          return undefined;
        }
        return widget.render({ formItem, formData, formErrors, setValue });
      }
      default:
        console.error(
          `Don't know how to handle form input type ${(formItem as { type: string }).type}`,
        );
    }
  }, [formItem, formData, formErrors, setValue, resolvedOptions]);

  const errorText = useMemo(() => {
    const formItemErrors = formErrors[formItem.name];
    if (
      !formItemErrors ||
      isEmpty(formItemErrors) ||
      formItemErrors.every((validationResult) => validationResult.valid)
    )
      return undefined;
    return formItemErrors
      .filter((validationResult) => !validationResult.valid)
      .map((validationResult) => validationResult.message)
      .join("\n");
  }, [formItem.name, formErrors]);

  return (
    <div
      className="_form-input.tsx form-input"
      key={formItem.name}
      id={`form-input-${formItem.name}`}
    >
      {LayoutService.get()
        .get(formLayout)
        ?.layoutFormItem({
          label: formItem.label ?? formItem.name,
          helperText: formItem.helpText,
          input: formInput!,
          errorText: errorText,
        })}
    </div>
  );
};
